"use client";

import { toPlainDateString } from "@sa-nu/shared-utils/date";
import { useCallback, type ReactNode } from "react";

import { CheckoutDatePickerDrawer } from "../../../_components/reservation-form/reservation-calendar/checkout-date-picker-drawer";
import type {
  CellSelectionContinuation,
  CellSelectionPayload,
} from "../../../_components/reservation-form/reservation-calendar/reservation-flow-override-context";
import { ReservationFlowOverrideProvider } from "../../../_components/reservation-form/reservation-calendar/reservation-flow-override-context";
import { useRoomDetailDrawer } from "../../../_components/room-detail/room-detail-drawer-provider";

/**
 * `/ticket-reservations/form?new=1` 用の配線コンポーネント。
 *
 * - セルタップ → 即 Drawer open（部屋詳細 + availability を表示）
 * - 「予約する」押下 → 部屋詳細 Drawer にネストした CheckoutDatePickerDrawer を開く
 * - 部屋番号タップ → availability 無しで Drawer open（部屋詳細のみ閲覧）。
 *   CTA は「閉じる」になる。
 *
 * `RoomDetailDrawerProvider` の内側に置くこと。
 *
 * Bridge が渡すのは識別子 (`accommodationId` / `firstNight` / `provider`) のみ。
 * 部屋詳細・availability の fetch は Drawer 側 (`RoomDetailDrawerQuery`) が行う。
 * CheckoutDatePickerDrawer の組み立ては Bridge が行い、reservation-calendar は
 * チケット固有 UI に依存しない。
 */
export function TicketRoomDetailFlowBridge({
  children,
}: {
  children: ReactNode;
}) {
  const { open } = useRoomDetailDrawer();

  const onCellSelected = useCallback(
    (
      payload: CellSelectionPayload,
      { onReservationDraftSelected }: CellSelectionContinuation,
    ) => {
      // チェックアウト日選択 Drawer のネスト経路: 部屋詳細を閉じずに上へ被せる
      open({
        accommodationId: payload.accommodationId,
        availability: {
          firstNight: payload.date,
          provider: payload.provider,
          nestedDrawer: {
            render: ({ open, onOpenChange, closeAll, anchorEl }) => (
              <CheckoutDatePickerDrawer
                key={`${payload.accommodationId}-${toPlainDateString(payload.date)}`}
                open={open}
                onOpenChange={onOpenChange}
                accommodationId={payload.accommodationId}
                firstNight={payload.date}
                provider={payload.provider}
                minNight={payload.minNight}
                maxNight={payload.maxNight}
                anchorEl={anchorEl}
                onSelect={(selected) => {
                  onReservationDraftSelected(selected);
                  closeAll();
                }}
              />
            ),
          },
        },
      });
    },
    [open],
  );

  // 部屋番号タップ: availability 無しで Drawer を開く → CTA は「閉じる」。
  const onAccommodationSelected = useCallback(
    (accommodationId: string) => {
      open({ accommodationId });
    },
    [open],
  );

  return (
    <ReservationFlowOverrideProvider
      onCellSelected={onCellSelected}
      onAccommodationSelected={onAccommodationSelected}
    >
      {children}
    </ReservationFlowOverrideProvider>
  );
}
